import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { RoutePath } from "@/routes";
import { useAppData } from "@/contexts/AppDataContext";
import { brandApi } from "@/resources/brand";
import {
  useGetBrandIntelligenceCategoryChoice, useGetBrandIntelligenceMatch, useMatchBrand, useRefreshBrandIntelligence,
  useSaveBrandIntelligenceCategoryChoice, type BiBrandMatch,
} from "@/resources/brand-intelligence/brand-intelligence.api";
import { CategoryChoiceSections, type CategoryChoiceRow, type ProductsStatus } from "@/components/brand-intelligence/CategoryChoiceSections";
import { handleApiError } from "@/utils";
import { toast } from "@/hooks/use-toast";
import { useContentBelowFold } from "@/hooks/useContentBelowFold";

// AUT-245 §11 / AUT-499: match the typed name, ask for the primary category or niche, then create
// the brand, store the choice and start the full-catalog build. With `brandId` it is the edit
// dialog instead: the report's own categories, the stored choice, and Save.

interface AddBrandModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId?: number | null;
  brandName?: string;
  reportCategories?: CategoryChoiceRow[];
  onSaved?: () => void;
  /** Edit mode: the element focus returns to on close. Radix only restores focus to a DialogTrigger, and this dialog has none. */
  returnFocusRef?: { current: HTMLElement | null };
}

type Step = "entry" | "matching" | "match" | "no-match";

// Human ruling 2026-10-13 (intent.md Open question 2): one toast, worded by which step failed.
const CHOICE_FAILED =
  "Brand added, but its primary category was not saved. Choose it from the Brand Intelligence header once the report is ready.";
const BUILD_FAILED = "Brand added, but the build could not be started. Use Refresh on Brand Intelligence to build it.";
const BOTH_FAILED =
  "Brand added, but its primary category was not saved and the build could not be started. Use Refresh on Brand Intelligence to build it, then choose the category from the header.";
// Design capture: the footer shadows the scroll area only while content sits below the fold.
const FOLD_SHADOW = "shadow-[0_-8px_8px_-6px_hsl(var(--foreground)/0.18)]";

// Highest revenue first; ties by the raw name under a code-unit comparison (not localeCompare).
const byRevenue = (a: CategoryChoiceRow, b: CategoryChoiceRow) =>
  b.brand_revenue - a.brand_revenue || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0);

export function AddBrandModal({ open, onOpenChange, brandId, brandName, reportCategories, onSaved, returnFocusRef }: AddBrandModalProps) {
  const mode = brandId != null ? "edit" : "add";
  const navigate = useNavigate();
  const { setSelectedBrandId } = useAppData();
  const createBrand = brandApi.useCreateBrand();
  const match = useMatchBrand();
  const saveChoice = useSaveBrandIntelligenceCategoryChoice();
  const refresh = useRefreshBrandIntelligence();

  const [step, setStep] = useState<Step>("entry");
  const [name, setName] = useState("");
  const [matchData, setMatchData] = useState<BiBrandMatch | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const fold = useContentBelowFold();
  const choice = useGetBrandIntelligenceCategoryChoice(brandId ?? null);
  const editMatch = useGetBrandIntelligenceMatch(brandId ?? null, brandName ?? "", mode === "edit" && open);

  // Seed on the open transition, not in handleClose: a brand switch flips `open` without calling it.
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open && mode === "edit") setChecked(new Set((choice.data?.categories ?? []).map((c) => c.leaf_category_id)));
  }

  const reset = () => {
    setStep("entry");
    setName("");
    setMatchData(null);
    setChecked(new Set());
    setSubmitting(false);
  };

  const handleClose = () => (reset(), onOpenChange(false));

  const handleLookup = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setStep("matching");
    match.mutate({ name: trimmed }, {
      onSuccess: (data) => {
        setMatchData(data.matched ? data : null);
        setChecked(new Set((data.categories ?? []).filter((c) => c.default_checked).map((c) => c.leaf_category_id)));
        setStep(data.matched ? "match" : "no-match");
      },
      onError: () => setStep("no-match"),
    });
  };

  const goToBrand = (brandId: number) => {
    setSelectedBrandId(brandId);
    navigate(RoutePath.BrandIntelligence);
    handleClose();
  };

  const addAnyway = () =>
    createBrand.mutate({ name: name.trim() }, { onSuccess: (newBrand) => goToBrand(newBrand.id) });

  // Confirm and build: brand, then the choice, then the build. The build starts whether or not the
  // choice was stored, and a failure of either shows one toast at the call site.
  const confirmAndBuild = async () => {
    if (!matchData) return;
    setSubmitting(true);
    const created = await createBrand.mutateAsync({ name: matchData.brand ?? name.trim() }).catch(() => null);
    if (!created) return setSubmitting(false);
    const brandId = created.id;
    const ok = () => true;
    const failed = () => false;
    const choiceSaved = await saveChoice.mutateAsync({ brandId, leafCategoryIds: chosenIds() }).then(ok, failed);
    const buildStarted = await refresh.mutateAsync({ brandId, acknowledge: true }).then(ok, failed);
    goToBrand(brandId);
    if (!choiceSaved || !buildStarted) {
      const description = choiceSaved ? BUILD_FAILED : buildStarted ? CHOICE_FAILED : BOTH_FAILED;
      toast({ description, variant: "destructive" });
    }
  };

  const save = () =>
    saveChoice.mutate({ brandId: brandId as number, leafCategoryIds: chosenIds() }, {
      onSuccess: () => {
        handleClose();
        const k = checked.size;
        const description = k === 1 ? "Brand Intelligence will use the new primary category." : `Brand Intelligence will blend data across ${k} categories.`;
        toast({ title: "Category updated", description });
        onSaved?.();
      },
      onError: (error) => handleApiError(error),
    });

  const toggle = (id: string) =>
    setChecked((prev) => (prev.has(id) ? new Set([...prev].filter((x) => x !== id)) : new Set([...prev, id])));

  const edit = mode === "edit";
  const chosenIds = () => categories.filter((c) => checked.has(c.leaf_category_id)).map((c) => c.leaf_category_id);
  const view = edit ? "match" : step;
  const categories = edit ? [...(reportCategories ?? [])].sort(byRevenue) : matchData?.categories ?? [];
  const busy = submitting || saveChoice.isPending;
  const productsStatus: ProductsStatus =
    !edit || editMatch.data?.matched ? "ready" : editMatch.isError || editMatch.data ? "error" : "loading";
  const brand = <span className="font-semibold text-foreground-strong">{edit ? brandName : matchData?.brand}</span>;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent
        className={cn(view === "match" ? "sm:max-w-3xl" : "sm:max-w-md")}
        onOpenAutoFocus={edit ? (e) => e.preventDefault() : undefined}
        onCloseAutoFocus={
          edit
            ? (e) => {
                const target = returnFocusRef?.current;
                if (!target) return;
                e.preventDefault();
                target.focus();
              }
            : undefined
        }
      >
        {view === "entry" && (
          <>
            <DialogHeader>
              <DialogTitle>Add New Brand</DialogTitle>
              <DialogDescription>We'll look this name up against Amazon catalog data before building anything.</DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <div className="space-y-1.5">
                <Label htmlFor="brand-name" className="text-xs">Brand Name</Label>
                <Input
                  id="brand-name" placeholder="Enter brand name" value={name} className="h-9 rounded-lg bg-card"
                  onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLookup()}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline-on-white" onClick={handleClose} className="rounded-lg">Cancel</Button>
              <Button onClick={handleLookup} disabled={!name.trim()} className="rounded-lg">Find brand</Button>
            </DialogFooter>
          </>
        )}

        {view === "matching" && (
          <>
            <DialogHeader><DialogTitle>Matching "{name.trim()}"</DialogTitle></DialogHeader>
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Looking up catalog data…
            </div>
          </>
        )}

        {view === "match" && (edit || matchData) && (
          <>
            <DialogHeader>
              <DialogTitle>{edit ? "Change primary category or niche" : "Confirm brand match"}</DialogTitle>
              <DialogDescription>
                {edit ? (
                  <>Choose the market data Brand Intelligence uses for {brand}.</>
                ) : (
                  <>
                    We matched "{name.trim()}" to {brand} in the Amazon catalog. Review the data below and then build the
                    brand.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            <ScrollArea ref={fold.ref} className="max-h-[55vh] pr-4">
              <CategoryChoiceSections
                categories={categories} checkedIds={checked} onToggle={toggle} productsStatus={productsStatus}
                products={edit ? editMatch.data?.products : matchData?.products}
              />
            </ScrollArea>

            <DialogFooter className={cn("-mx-6 -mb-6 bg-card px-6 pb-6 pt-4 sm:rounded-b-[12px]", fold.below && FOLD_SHADOW)}>
              <Button variant="outline-on-white" onClick={edit ? handleClose : reset} disabled={submitting} className="rounded-lg">
                {edit ? "Cancel" : "This isn't right"}</Button>
              <Button onClick={edit ? save : confirmAndBuild} disabled={checked.size === 0 || busy} className="gap-1.5 rounded-lg">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {edit ? "Save" : "Confirm and build"}
              </Button>
            </DialogFooter>
          </>
        )}

        {view === "no-match" && (
          <>
            <DialogHeader><DialogTitle>No catalog match found</DialogTitle></DialogHeader>
            <Alert variant="info">
              <Search className="h-4 w-4" />
              <AlertTitle>Brand not found</AlertTitle>
              <AlertDescription>
                No matching Amazon catalog data for "{name.trim()}". Try a different spelling, or add it anyway and
                build the catalog manually from the Products page.
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button variant="outline-on-white" onClick={() => setStep("entry")} className="rounded-lg">Try a different name</Button>
              <Button onClick={addAnyway} disabled={createBrand.isPending} className="gap-1.5 rounded-lg">
                {createBrand.isPending && <Loader2 className="h-4 w-4 animate-spin" />}Add anyway
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
